/* tslint:disable:quotemark */
import {
    AfterViewInit,
    Component,
    computed,
    effect,
    inject,
    model,
    ModelSignal,
    signal,
    Signal,
    viewChild,
    WritableSignal,
} from '@angular/core';
import {Report} from '../../classes/report';
import {ReportService} from '../../services/report.service';
import {GetNormalOutageDataArguments, NormalDataService} from 'frontend/src/app/services/normal.service';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {GenAggregationLevel} from 'frontend/generated/serverModels/GenAggregationLevel';
import {MatDialog} from '@angular/material/dialog';
import {
    LocationFilterModalComponent,
    LocationFilterModalDataOptions,
} from 'frontend/src/shared/modals/location-filter-modal/location-filter-modal.component';
import {AggregationLevel, LocationData, VulcanDateTimePickerConfig, VulcanDateTimePickerForm} from '@eagle-i/vulcan-component-library';
import {DateTimePickerModalComponent} from 'frontend/src/shared/modals/date-time-picker-modal/date-time-picker-modal.component';
import {FormControl, FormGroup} from '@angular/forms';
import moment, {Moment} from 'moment';
import {DataService} from 'frontend/src/app/services/data.service';
import {State} from '../../../outage/classes/state';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {ApplicationConfig, IGeometry} from 'frontend/src/app/classes/application-config';
import {GenOutageAggregationLevel} from 'frontend/generated/serverModels/GenOutageAggregationLevel';
import {FileDownload} from 'frontend/src/app/classes/file-download';
import {filter} from 'rxjs';
import {NormalOutageDataSource} from '../../../layer/sources/normal-outage-source';
import {MapOptions} from '../../../map/classes/map-options';
import {GenServiceType} from 'frontend/generated/serverModels/GenServiceType';
import {LeafletMapLayer} from '../../../layer/classes/leaflet-map-layer';
import {NormalOutageData} from '../../../layer/classes/normal-outage-data';
import {state} from '@angular/animations';
import {MatCheckboxChange} from '@angular/material/checkbox';

@Component({
    selector: 'eaglei-normal-report',
    templateUrl: './normal-report.component.html',
    styleUrls: ['../reports.scss', './normal-report.component.scss'],
    standalone: false,
})
export class NormalReportComponent extends Report<NormalOutageData> implements AfterViewInit {
    // view children
    private paginator = viewChild(MatPaginator);
    private sort = viewChild(MatSort);

    // Services
    private reportService = inject(ReportService);
    private normalService = inject(NormalDataService);
    private dialog = inject(MatDialog);
    private dataService = inject(DataService);

    // Filters
    public level: WritableSignal<GenAggregationLevel> = signal(GenAggregationLevel.state);
    private defaultStart = moment().subtract(2, 'days').startOf('day');
    private defaultEnd = ApplicationConfig.roundMinute();

    public dateRangeForm = new FormGroup<VulcanDateTimePickerForm>({
        start: new FormControl(this.defaultStart.toDate()),
        end: new FormControl(this.defaultEnd.toDate()),
    });
    public selectedDates = toSignal(this.dateRangeForm.valueChanges);
    private formSignal = toSignal(this.dateRangeForm.valueChanges);
    public startAsMoment: Signal<Moment> = computed(() => {
        const form = this.formSignal();
        return form?.start ? moment(form?.start) : this.defaultStart;
    });
    public endAsMoment = computed(() => {
        const form = this.formSignal();
        return form?.end ? moment(form?.end) : this.defaultEnd;
    });

    // Table configuration
    public dataSource = new MatTableDataSource<NormalOutageData>();
    private sharedColumns: string[] = ['lastCalculationDate', 'normalValue', 'countAboveNormal'];
    private stateColumns: string[] = ['stateName', ...this.sharedColumns];
    private countyColumns: string[] = ['stateName', 'countyName', ...this.sharedColumns];

    private utilityColumns: string[] = ['utilityName', 'subUtilityName', 'stateName', 'countyName', ...this.sharedColumns];
    public columns = computed(() => {
        switch (this.level()) {
            case GenAggregationLevel.state:
                return this.stateColumns;
            case GenAggregationLevel.county:
                return this.countyColumns;
            case GenAggregationLevel.utility:
                return this.utilityColumns;
        }
    });

    /**
     * State and county information. Uses to keep track of what states are selected.
     *
     * The location filter returns the fips codes but the requests require state abbreviations.
     * We also have to handle county fips codes.
     */
    private allStates: Signal<State[]> = toSignal(this.dataService.stateNames);
    private allCounties: Signal<IGeometry[]> = signal(ApplicationConfig.geometries.get(GenOutageAggregationLevel.county));
    private mapOfAllCounties: Signal<Map<string, IGeometry>> = computed(() => {
        const counties = this.allCounties();
        const map = new Map<string, IGeometry>();

        counties.forEach((c) => map.set(c.fipsCode, c));
        return map;
    });
    private selectedStateAbbreviations: WritableSignal<string[]> = signal(this.allStates().map((s) => s.abbreviation));
    public selectedStates: Signal<State[]> = computed(() => {
        const selectedStateCodes = this.selectedStateAbbreviations();

        return this.allStates().filter((s) => selectedStateCodes.includes(s.abbreviation));
    });
    private mapOfSelectedStates: Signal<Map<string, State>> = computed(() => {
        const selectedStates = this.selectedStates();
        const map = new Map<string, State>();

        selectedStates.forEach((selectedState) => {
            map.set(selectedState.geoid, selectedState);
        });

        return map;
    });
    // This is used to prep the location selector with the right codes. If the level is county we need county codes, not state
    public selectedStateFipsCodes: Signal<string[]> = computed(() => {
        const level = this.level();
        const statesCodes = this.selectedStates().map((s) => s.geoid);
        const counties = this.allCounties();

        // // If the level is "county" the location selector will return county codes. We have to convert those back to state codes
        if (level === GenAggregationLevel.county) {
            return counties.filter((c) => statesCodes.includes(c.fipsCode.slice(0, 2))).map((c) => c.fipsCode);
        }
        return statesCodes;
    });

    // Chart
    public selectedState: WritableSignal<State[]> = signal([]);
    public selectedNormalOutage: Signal<number> = computed(() => {
        const selectedState = this.selectedState()?.[0];
        const outageData = this.dataSource.data.find((d) => d.fipsCode.slice(0, 2) === selectedState.geoid);

        return outageData?.normalValue;
    });
    public chartHeader = computed(() => {
        const selectedState = this.selectedState();

        if (this.showChart() && selectedState[0]?.name) {
            return `Normal Outage Comparison for ${selectedState[0]?.name}`;
        }

        return '';
    });
    public showChart: Signal<boolean> = computed(() => {
        const selectedStates = this.selectedState();
        const level = this.level();
        return level === GenAggregationLevel.state && selectedStates.length > 0;
    });
    public chartMask: Signal<string> = computed(() => {
        const level = this.level();
        if (level === GenAggregationLevel.state) {
            return 'Select a state to see outage level compared to normal levels';
        }
        return 'The chart can only compare state outage levels.';
    });

    // Map
    public mapOptions: MapOptions = new MapOptions().setZoom(3, 1).setCenter(37.2, -92);
    private layerSource!: NormalOutageDataSource;
    public colorScale = this.normalService.colorScale;
    public scaleHeader: WritableSignal<'Normal Value' | 'Count Above Normal'> = signal('Normal Value');
    public showMap = computed(() => {
        return this.level() !== GenAggregationLevel.utility;
    });
    public showCountAboveNormal: ModelSignal<boolean> = model(false);

    constructor() {
        super();

        this.buildLayerSource();

        // Subscribe to the normal outage data stored in the service
        this.normalService.normalOutageData.pipe(takeUntilDestroyed()).subscribe((data) => {
            const states = this.mapOfSelectedStates();
            const counties = this.mapOfAllCounties();

            // Add the boundary from the State object to the NormalOUtageData object
            data.forEach((geometry) => {
                if (this.level() === GenAggregationLevel.state) {
                    geometry.boundary = states.get(geometry.fipsCode)?.boundary;
                } else {
                    // show utilities at the county level
                    geometry.boundary = counties.get(geometry.fipsCode)?.boundary;
                }
            });
            this.dataSource.data = data;

            this.layerSource.removeFromMap();

            // If no states are selected just reset to CONUS
            if (this.dataSource.data.length) {
                this.layerSource.processFeatures(this.dataSource.data);
                this.layerSource.addToMap();
                // this causes a problem if Alaska is selected because it crosses the date line.
                // We might want to remove
                this.layerSource.fitToFeatures();
            } else {
                this.layerSource.fitToConus();
            }
        });

        this.mapOptions.onlyManualZoom = true;
        this.mapOptions.show = {
            sidebar: false,
            refresh: false,
            export: false,
            coverage: false,
            coordinate: false,
            zoom: true,
            panControl: true,
        };
    }

    ngOnInit() {
        // Get info about the report from the DB
        this.reportService.getReportData().subscribe((res) => this.initializeReportInfo(res));

        // Load initial data. Defaults to state
        this.getNormalOutageData();
    }

    ngAfterViewInit(): void {
        // Set table tools
        this.dataSource.paginator = this.paginator();
        this.dataSource.sort = this.sort();
        this.sort().active = 'stateName';
    }

    private buildLayerSource() {
        const layer = new LeafletMapLayer({
            displayName: 'Normal Outage Data',
            servicetype: GenServiceType.VECTOR,
            uiHandle: 'normal-outage-data',
            attributionTitle: '',
            attributionUrl: '',
        } as any);

        this.layerSource = new NormalOutageDataSource(layer);
    }

    private getNormalOutageData() {
        this.showCountAboveNormal.set(false);
        const config: GetNormalOutageDataArguments = {
            level: this.level().toString(),
            start: this.dateRangeForm.controls.start.value,
            end: this.dateRangeForm.controls.end.value,
            state: this.selectedStateAbbreviations(),
        };
        this.normalService.getNormalOutageData(config);
    }

    public openFilterDialog() {
        const config: LocationFilterModalDataOptions = {
            autoSelectCodes: this.selectedStateFipsCodes(),
            autoSelectAggregationLevel: this.level().toString() as AggregationLevel,
            showAggregationLevels: true,
            showCounties: false,
            aggregationLevels: ['state', 'county', 'utility'],
        };
        const ref = this.dialog.open(LocationFilterModalComponent, {
            data: config,
        });

        ref.afterClosed()
            .pipe(filter((data) => !!data))
            .subscribe((data: LocationData) => {
                this.level.set(GenAggregationLevel.forName(data.aggregationLevel));

                // Update the default sort when we change the aggregation level
                switch (this.level()) {
                    case GenAggregationLevel.state:
                        this.sort().active = 'stateName';
                        break;
                    case GenAggregationLevel.county:
                        this.sort().active = 'countyName';
                        break;
                    case GenAggregationLevel.utility:
                        this.sort().active = 'utilityName';
                        break;
                }
                // If the level is county we need to trim the codes down to get the state codes
                // use [...new Set()] to remove duplicates
                const trimmedCodes = [...new Set(data.codes.map((c) => c.slice(0, 2)))];
                // Get the abbreviations for the selected states
                const selectedAbbreviations = this.allStates()
                    .filter((s) => trimmedCodes.includes(s.geoid))
                    .map((s) => s.abbreviation);
                this.selectedStateAbbreviations.set(selectedAbbreviations);
                this.getNormalOutageData();
            });
    }

    public openDateRangeDialog() {
        const config: {
            config: VulcanDateTimePickerConfig;
            form: FormGroup<VulcanDateTimePickerForm>;
            title: string;
        } = {
            title: 'Set Date Range',
            config: {
                includeTime: true,
                dateType: 'range',
                // We're assuming that our max end time and date should be the current date
                endMaxDate: ApplicationConfig.roundMinute().toDate(),
            },
            form: this.dateRangeForm,
        };
        const ref = this.dialog.open(DateTimePickerModalComponent, {
            data: config,
            maxWidth: '720px',
        });

        ref.afterClosed()
            .pipe(filter((data) => !!data))
            .subscribe(() => {
                this.getNormalOutageData();
            });
    }

    // How to update the selected states if the filter chips are toggled
    public updateSelectedStates(states: State[]) {
        this.selectedStateAbbreviations.set(states.map((s) => s.abbreviation));
        this.getNormalOutageData();
    }

    /**
     * Exports the table as a CSV File
     */
    public exportTable(): void {
        let data: string =
            this.columns()
                .map((c) => c.toLocaleUpperCase())
                .join() + '\n';
        this.dataSource.filteredData.forEach((normalOutage) => {
            const info: string[] = [];

            this.columns().forEach((column) => {
                const formatted = FileDownload.formatCsvCell(normalOutage[column]);
                info.push(formatted);
            });

            data += `${info.join()}\n`;
        });

        FileDownload.downloadCSV(`normalOutageData_${this.level()}`, data, this.attributionUrl);
    }

    public setSelectedStateForChart(newSelectedState: State) {
        const found = this.selectedStates().find((s) => s.stateAbbreviation === newSelectedState.stateAbbreviation);
        this.selectedState.set([found]);
    }

    // This gets the range of values used on the legend.
    public colorRange(color: string): string {
        return this.normalService.colorRange(color);
    }

    // Toggle between using the Count Above Normal and Normal Value columns
    public toggleData(change: MatCheckboxChange) {
        this.layerSource.removeFromMap();

        this.layerSource.processFeatures(this.dataSource.data, change.checked ? 'countAboveNormal' : 'normalValue');
        this.layerSource.addToMap();

        this.scaleHeader.set(change.checked ? 'Count Above Normal' : 'Normal Value');
    }
}
